import { logOperationalEvent } from 'saddlebag-logger';

import { MessageBuffer } from './messageBuffer';
import { getEventHeader, isDevEnv } from './sharedFuncs';

export const FETCH_MECHANISM = 'fetch';
export const SENDBEACON_MECHANISM = 'sendBeacon';
const SLIPSTREAM_ENDPOINT = '/slipstream';
const SLIPSTREAM_ABSOLUTE_ENDPOINT =
  'https://slipstream.skyscanner.net/slipstream';
const MAX_BUFFER_SIZE = 100000; // bytes
const FLUSH_AFTER = 200; // milliseconds
const FLUSH_ON_EXIT = false;

const sendBeacon = (url, payload) => {
  let result;
  try {
    result = navigator.sendBeacon(url, payload);
  } catch (error) {
    result = false;
  }
  if (!result) {
    if (isDevEnv()) {
      // Additional logging when in development.
      // eslint-disable-next-line no-console
      console.error(`Beacon to ${url} failed. Request payload: ${payload}.`);
    }
    logOperationalEvent({
      eventName: 'grapplerLogger_beacon_error',
    });
  }
  return result;
};

const sendFetch = (url, payload) =>
  fetch(url, {
    method: 'POST',
    body: payload,
    headers: {
      'Content-Type': 'application/json',
    },
  }).catch((rejectionReason) => {
    if (isDevEnv()) {
      // Additional logging when in development.
      // eslint-disable-next-line no-console
      console.error(
        `${rejectionReason}. Request to ${url} failed. Request payload: ${payload}.`,
      );
    }
    logOperationalEvent({
      eventName: 'grapplerLogger_fetch_error',
      handledError: rejectionReason.message,
      // Capturing additional fields to help diagnose issue.
      fetchUrl: url,
      fetchPayload: payload,
    });
  });

const sendToSlipstream = (url, message, options) => {
  if (options.mechanism === FETCH_MECHANISM) {
    return sendFetch(url, message);
  }
  const result = sendBeacon(url, message);
  if (!result) {
    logOperationalEvent({ eventName: 'grapplerLogger_fallback_used' });
    return sendFetch(url, message);
  }
  return Promise.resolve(true);
};

const sendBatchToSlipstream = (url, messages, options) => {
  const serializedMessages = messages.map((currentMessage) => {
    const message = options.enhance
      ? JSON.stringify({ ...currentMessage, event_header: getEventHeader() })
      : JSON.stringify(currentMessage);
    return message;
  });

  const joinedSerializedMessages = serializedMessages.join('');
  if (options.mechanism === FETCH_MECHANISM) {
    return sendFetch(url, joinedSerializedMessages);
  }
  const result = sendBeacon(url, joinedSerializedMessages);
  if (!result) {
    logOperationalEvent({ eventName: 'grapplerLogger_fallback_used' });
    return sendFetch(url, joinedSerializedMessages);
  }
  return Promise.resolve();
};

/**
 * Class representing the GrapplerLogger used in web.
 */
class GrapplerLogger {
  /**
   * @public
   * @param {string} env - The environment that GrapplerLogger should use. Either "public" or "public-sandbox".
   * @param {string} serviceName - The service that is using GrapplerLogger. Typically this is a microsite name, but may also be a library/namespace.
   * @param {object} [options] - Options that configure the behaviour of this instance of GrapplerLogger.
   * @param {boolean} [options.enhance=true] - Default `true`. See: {@link GrapplerLogger.enhance}
   * @param {'fetch' | 'sendBeacon'} [options.mechanism='fetch'] - Default `fetch`. See: {@link GrapplerLogger.mechanism}
   * @param {boolean} [options.useAbsoluteUrl=false] - Default `false`. When true, tracking requests use absolute URL (https://slipstream.skyscanner.net/slipstream) instead of relative URL (/slipstream).
   * @param {boolean} [options.batching.enabled=false] - Default false. See: {@link GrapplerLogger.preferBatching}
   * @param {number} [options.batching.maxBufferSize=100000] - Default 100000. Suitable for fetch but not sendBeacon which has a limit of around 64Kb on some browsers.
   * @param {number} [options.batching.flushAfter=200] - Default 200 milliseconds. Can be increased to capture more events.
   * @param {boolean} [options.batching.flushOnExit=false] - Default false. If true, the buffer will be flushed on page exit.
   */

  /**
   * @type {MessageBuffer} - Default `undefined`.
   * @public
   * The queue of messages to be sent to Slipstream grouped by url (same event types).
   */
  queue;

  constructor(
    env,
    serviceName,
    {
      batching = {
        maxBufferSize: MAX_BUFFER_SIZE,
        enabled: false,
        flushAfter: FLUSH_AFTER,
        flushOnExit: FLUSH_ON_EXIT,
      },
      enhance = true,
      mechanism = FETCH_MECHANISM,
      useAbsoluteUrl = false,
    } = {},
  ) {
    if (env !== 'public' && env !== 'public-sandbox') {
      throw new Error(
        'GrapplerLogger environment must be "public" or "public-sandbox"',
      );
    }
    if (mechanism !== FETCH_MECHANISM && mechanism !== SENDBEACON_MECHANISM) {
      throw new Error(
        `Unsupported transmission mechanism passed. ${mechanism} is not supported, please choose fetch or sendBeacon.`,
      );
    }
    this.env = env;
    this.serviceName = serviceName;
    this.endpoint = useAbsoluteUrl
      ? SLIPSTREAM_ABSOLUTE_ENDPOINT
      : SLIPSTREAM_ENDPOINT;
    /**
     * @type boolean
     * @public
     * When set to true will include the `event_header` property on any events sent automatically.
     */
    this.enhance = enhance;
    /**
     * @type 'fetch' | 'sendBeacon'
     * @public
     * When set to fetch events will be sent via fetch. When set to sendBeacon events will be sent via sendBeacon.
     * - fetch is useful for larger payloads but can be terminated early if a user navigates from a page.
     * - sendBeacon is sent asynchronously and doesn't impact the loading of the next page. It has a payload limit on most browsers of around 64Kb.
     */
    this.mechanism = mechanism;
    /**
     * @type boolean
     * @public
     * When set to true events will be queued before sending. Having this set and followed up with multiple events of the same type can be more efficient.
     */
    this.preferBatching = batching.enabled || false;
    if (this.preferBatching) {
      this.queue = new MessageBuffer({
        maxBufferSize: batching.maxBufferSize || MAX_BUFFER_SIZE,
        flushAfter:
          batching.flushAfter > -1 ? batching.flushAfter : FLUSH_AFTER,
        flushOnExit: batching.flushOnExit || FLUSH_ON_EXIT,
        callbackOpts: { mechanism: this.mechanism, enhance: this.enhance },
        flushCallback: sendToSlipstream,
      });
    }
  }

  sendBaseEvent(url, message, options) {
    // we serialize here rather than all together this allows us to split the queue into smaller batches if needed
    // I also think it's more efficient to serialize as we go rather than all at once
    let serialized;
    if (this.enhance) {
      const enhanced = { ...message, event_header: getEventHeader() };
      serialized = JSON.stringify(enhanced);
    } else {
      serialized = JSON.stringify(message);
    }
    // if preferBatching is enabled, queue the message and return a promise
    if (this.preferBatching) {
      return this.queue.addMessage(url, serialized, options.priority);
    }

    return sendToSlipstream(url, serialized, {
      mechanism: this.mechanism,
    });
  }

  /**
   * @param {string} eventName - Custom namespace. This will be included within the path of the slipstream url used when sending this event.
   * @param {string} schemaName - full schema name that matches the message that is being sent.
   * @param {object} message payload to be sent.
   * @param {object} options  - set of options to be passed to the sendBaseEvent function.
   * @param {boolean} options.priority - defined for when batching is in use and you want to send the message immediately.
   * @returns {Promise} promise indicating success or failure of the sent event.
   */
  sendEvent(eventName, schemaName, message, options = { priority: false }) {
    const url = `${this.endpoint}/grp/v1/custom/${this.env}/${this.serviceName}/${eventName}/${schemaName}`;
    return this.sendBaseEvent(url, message, options);
  }

  /**
   * @param {string} eventName - Custom namespace. This will be included within the path of the slipstream url used when sending this event.
   * @param {string} schemaName - full schema name that matches the message that is being sent.
   * @param {object[]} messages payloads to be sent.
   * @returns {Promise} promise indicating success or failure of the sent events.
   */
  sendEvents(eventName, schemaName, messages) {
    const url = `${this.endpoint}/grp/v1/custom/${this.env}/${this.serviceName}/${eventName}/${schemaName}`;
    return sendBatchToSlipstream(url, messages, {
      enhance: this.enhance,
      mechanism: this.mechanism,
    });
  }

  /**
   * @param {string} eventName - Custom namespace. This will be included within the path of the slipstream url used when sending this event.
   * @param {string} schemaName - full schema name that matches the message that is being sent.
   * @param {object} message payload to be sent.
   * @param {object} options  - set of options to be passed to the sendBaseEvent function.
   * @param {boolean} options.priority - defined for when batching is in use and you want to send the message immediately.
   * @returns {Promise} promise indicating success or failure of the sent event.
   */
  sendEventWithEncodedUrl(
    eventName,
    schemaName,
    message,
    options = { priority: false },
  ) {
    const encodedValue = `${btoa(
      `${this.serviceName}/${eventName}/${schemaName}`,
    )}`;
    const url = `${this.endpoint}/grp/v1/encoded/${this.env}/${encodedValue}`;
    return this.sendBaseEvent(url, message, options);
  }
}

export { GrapplerLogger };
